// Single announcer service (UX spec 11.4): polite and assertive live regions,
// coalesced. Callers must never pass transcript text or other sensitive content.
import { type ReactNode, createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';

type Politeness = 'polite' | 'assertive';
interface AnnouncerValue {
  announce: (message: string, politeness?: Politeness) => void;
}

const AnnouncerContext = createContext<AnnouncerValue>({ announce: () => undefined });

export function AnnouncerProvider({ children }: { children: ReactNode }) {
  const [polite, setPolite] = useState('');
  const [assertive, setAssertive] = useState('');
  const last = useRef<{ text: string; at: number }>({ text: '', at: 0 });
  const announce = useCallback((message: string, politeness: Politeness = 'polite') => {
    const now = Date.now();
    if (message === last.current.text && now - last.current.at < 2000) return;
    last.current = { text: message, at: now };
    const set = politeness === 'assertive' ? setAssertive : setPolite;
    set('');
    // Clearing first makes repeated identical messages announce again.
    setTimeout(() => set(message), 50);
  }, []);
  const value = useMemo(() => ({ announce }), [announce]);
  return (
    <AnnouncerContext.Provider value={value}>
      {children}
      <div className="visually-hidden" aria-live="polite" aria-atomic="true">
        {polite}
      </div>
      <div className="visually-hidden" aria-live="assertive" aria-atomic="true">
        {assertive}
      </div>
    </AnnouncerContext.Provider>
  );
}

export function useAnnouncer(): AnnouncerValue {
  return useContext(AnnouncerContext);
}
